import { Table, RoleGuard, useTable } from 'portal-commons';
import React, { useState, useMemo } from 'react';

import AddNewExceptionCsp from './AddNewExceptionCsp';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import ExceptionalCspsListingRow from './ExceptionalCspsListingRow';
import {
  globalGetService,
  globalDeleteService,
} from '../../../utils/globalApiServices';
import { RemotePagination } from '../../../types';

const headRows = [
  {
    id: 'cspName',
    label: 'EXCEPTION FOR DOWNSTREAM CNP',
    sortable: false,
  },
  {
    id: 'action',
    label: 'AUTOMATED ACTION',
    sortable: false,
  },
  {
    id: 'save',
    label: 'SAVE',
    sortable: false,
  },
  {
    id: 'updateDate',
    label: 'LAST UPDATED',
    sortable: false,
  },
];

const PAGE_SIZE = 5;

interface ExceptionalCsp {
  cspUid: string;
  action: string;
  enabled: boolean;
  createDate: number;
  updateDate: number;
  properties: any | null;
  cspName: string;
}

enum Scene {
  None = 'none',
  Add = 'add',
  Delete = 'delete',
}

export default function ExceptionalCsps() {
  const [scene, setScene] = useState(Scene.None);
  const [deleteTarget, setDeleteTarget] = useState('');
  const [hideDeleteTarget, setHideDeleteTarget] = useState(false);
  const table = useTable<ExceptionalCsp>(
    async (filter) => {
      const response = await globalGetService<RemotePagination<ExceptionalCsp>>(
        `dca/primaryDcaCspRule`,
        {
          ...filter,
          isDefaultRule: false,
          recordsPerPage: PAGE_SIZE,
        }
      ).then((response) => response?.data);
      setHideDeleteTarget(false);

      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || PAGE_SIZE,
      };
    },
    {
      deps: [],
      inMemory: true,
    }
  );

  const showDeleteConfirmation = (cspUid: string) => {
    setScene(Scene.Delete);
    setDeleteTarget(cspUid);
  };

  const deleteCsp = async () => {
    setHideDeleteTarget(true);
    setScene(Scene.None);
    const response = await globalDeleteService(
      `dca/primaryDcaCspRule/csp/${deleteTarget}`
    );

    if (response?.ok) {
      table.reload();
    } else {
      // restore the delete target if the delete fails
      setHideDeleteTarget(false);
    }
  };

  const tableData = useMemo(() => {
    const remoteList = table.tableData;
    return hideDeleteTarget
      ? remoteList?.filter((item) => item.cspUid !== deleteTarget)
      : remoteList;
  }, [table.tableData, hideDeleteTarget, deleteTarget]);

  return (
    <>
      <div
        className="exceptional-csps-listing-wrapper"
        data-testid="exceptionalCsps"
      >
        <RoleGuard
          feature="settings.addException"
          render={(hasPermission) => {
            return (
              <div className="add-new-btn-wrapper">
                <p>
                  <a
                    className={`primary-btn ${hasPermission ? '' : 'disabled'}`}
                    onClick={() => setScene(Scene.Add)}
                    data-testid="exceptionalCspsAddButton"
                  >
                    Add New
                  </a>
                </p>
              </div>
            );
          }}
        />
        <Table
          testId="exceptionalCspListingRow"
          disableHover
          loading={table.loading}
          headRows={headRows}
          emptyState="no csps to view"
          tableData={tableData?.map((item) => (
            <ExceptionalCspsListingRow
              key={item.cspUid}
              cspObj={item}
              deleteCsp={showDeleteConfirmation}
            />
          ))}
          filter={table.filter}
          pagination={table.pagination}
          handleChangePage={table.handleChangePage}
          createSortHandler={table.handleSorting}
        />
        <AddNewExceptionCsp
          open={scene === Scene.Add}
          handleClose={() => setScene(Scene.None)}
          fetchListOfExceptionalCsps={table.reload}
        />
      </div>

      <DeleteConfirmationDialog
        open={scene === Scene.Delete}
        onCancel={() => setScene(Scene.None)}
        onSubmit={deleteCsp}
      />
    </>
  );
}
